import React, { useState, useRef, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import html2pdf from "html2pdf.js";
import debounce from "lodash/debounce";
import {
  Plus,
  Trash2,
  Download,
  Printer,
  Image,
  Search,
  X,
  ChevronDown,
  Palette,
  Save,
} from "lucide-react";
import hsnApi from "../../../api/hsnApi";
import invoiceApi from "../../../api/invoiceApi";
import accountApi from "../../../api/accountApi";
import { toast } from "react-toastify";
import gstApi from "../../../api/gstApi";
import officeLocationApi from "../../../api/officeLocationApi";

// Invoice Templates - Inspired by Zoho Invoice Templates
const INVOICE_TEMPLATES = [
  { id: "standard", name: "Standard", color: "#3b82f6", description: "Clean and professional" },
  { id: "elegant", name: "Elegant", color: "#8b5cf6", description: "Sophisticated design" },
  { id: "bold", name: "Bold", color: "#ef4444", description: "Eye-catching style" },
  { id: "simple", name: "Simple", color: "#6b7280", description: "Minimalist approach" },
  { id: "corporate", name: "Corporate", color: "#0891b2", description: "Business formal" },
];

const InvoiceGenerator = () => {
  const token = useSelector((s) => s.auth?.access_token);
  const invoiceRef = useRef();
  const logoInputRef = useRef();

  // Template state
  const [selectedTemplate, setSelectedTemplate] = useState("standard");

  // Organization and Account state
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [accountSearch, setAccountSearch] = useState("");
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [loadingAccounts, setLoadingAccounts] = useState(false);

  // GST registrations for selected account
  const [customerGstOptions, setCustomerGstOptions] = useState([]);
  const [customerAddressOptions, setCustomerAddressOptions] = useState([]);

  // HSN search state
  const [hsnResults, setHsnResults] = useState({});
  const [showHsnDropdown, setShowHsnDropdown] = useState({});

  // Invoice data
  const [invoice, setInvoice] = useState({
    // From (Business)
    logo: "",
    businessName: "",
    businessGstin: "",
    businessAddress: "",
    businessCity: "",
    businessState: "",
    businessPincode: "",
    businessPhone: "",
    businessEmail: "",
    // To (Customer)
    customerName: "",
    customerGstin: "",
    customerAddress: "",
    customerCity: "",
    customerState: "",
    customerPincode: "",
    customerPhone: "",
    customerEmail: "",
    // Invoice details
    invoiceNumber: `INV-${Date.now().toString().slice(-8)}`,
    invoiceDate: new Date().toISOString().split("T")[0],
    dueDate: "",
    placeOfSupply: "",
    // Items
    items: [
      {
        id: 1,
        description: "",
        hsnCode: "",
        quantity: 1,
        rate: 0,
        taxRate: 18,
        amount: 0,
      },
    ],
    // Notes
    notes: "",
    terms: "Payment is due within 30 days of invoice date.",
  });

  // Fetch organizations on mount
  useEffect(() => {
    fetchOrganizations();
  }, [token]);

  const fetchOrganizations = async () => {
    if (!token) return;
    try {
      const res = await invoiceApi.getAllOrganizations(token);
      const orgList = res?.data || res || [];
      setOrganizations(Array.isArray(orgList) ? orgList : []);
      if (orgList.length > 0) {
        selectOrganization(orgList[0]);
      }
    } catch (error) {
      console.error("Error fetching organizations:", error);
    }
  };

  const selectOrganization = (org) => {
    setSelectedOrg(org);
    setInvoice((prev) => ({
      ...prev,
      businessName: org.name || "",
      businessGstin: org.gstNumber || "",
      businessAddress: org.address || "",
      businessCity: org.city || "",
      businessState: org.stateCode || "",
      businessPincode: org.pincode || "",
      businessPhone: org.phone || "",
      businessEmail: org.email || "",
    }));
  };

  // Account search with debounce
  const searchAccounts = useMemo(
    () =>
      debounce(async (searchTerm) => {
        if (!searchTerm || searchTerm.length < 2) {
          setAccounts([]);
          setShowAccountDropdown(false);
          return;
        }
        setLoadingAccounts(true);
        try {
          const res = await accountApi.searchAccounts(searchTerm, token);
          // Parse response: { success, data: [...accounts] }
          const accountList = res?.data || res?.content || res || [];
          setAccounts(Array.isArray(accountList) ? accountList : []);
          setShowAccountDropdown(true);
        } catch (error) {
          console.error("Error searching accounts:", error);
          setAccounts([]);
        } finally {
          setLoadingAccounts(false);
        }
      }, 300),
    [token]
  );

  const handleAccountSearch = (value) => {
    setAccountSearch(value);
    setInvoice((prev) => ({ ...prev, customerName: value }));
    searchAccounts(value);
  };

  const selectAccount = async (account) => {
    console.log("Account:----", account);

    setSelectedAccount(account);
    // setInvoice(prev=> ({...prev, account}));
    setAccountSearch(account.accountName || account.name || "");
    setShowAccountDropdown(false);

    // Extract business info
    const businessInfo = account.businessInformation;
    // Fetch GST registrations if businessInformation exists
    let gstRegs = [];
    if (businessInfo?.id) {
      try {
        const gstRes = await gstApi.getGstRegistrations(businessInfo.id, token);
        gstRegs = gstRes?.data || gstRes || [];
      } catch (error) {
        console.error("Error fetching GST registrations:", error);
      }
    }
    let gstAddress = [];
    if (businessInfo?.id) {
      try {
        const gstRes = await officeLocationApi.getOfficeLocations(businessInfo.id, token);
        gstAddress = gstRes?.data || gstRes || [];
      } catch (error) {
        console.error("Error fetching GST registrations:", error);
      }
    }

    console.log("gstRegs", gstRegs);
    console.log("gstAddress", gstAddress);


    setCustomerGstOptions(Array.isArray(gstRegs) ? gstRegs : []);
    setCustomerAddressOptions(Array.isArray(gstAddress) ? gstAddress : []);

    // Select first GST or empty
    const gstNumber = gstRegs.length > 0 ? gstRegs[0].gstin : "";
    const getAddress = gstAddress.length > 0 ? gstAddress[0] : "";

    setInvoice((prev) => ({
      ...prev,
      customerName: account.accountName || account.name || "",
      customerGstin: gstNumber,
      customerAddressId: getAddress.id || "",
      customerAddress: getAddress.address || "",
      customerCity: getAddress.city || "",
      customerState: getAddress.state || "",
      customerPincode: getAddress.pincode || "",
      customerPhone: account.phoneNumber || account.mobileNumber || account.phone || "",
      customerEmail: account.email || "",
    }));
  };

  const handleGstSelection = (gstin) => {
    setInvoice((prev) => ({ ...prev, customerGstin: gstin }));
  };
  const handleAddressSelection = (index) => {
    const getAddress = customerAddressOptions.find((prev) => prev.id === index);

    setInvoice((prev) => ({
      ...prev,
      customerAddressId: index === "0" ? "" : getAddress.id,
      customerAddress: index === "0" ? "" : getAddress.address,
      customerCity: index === "0" ? "" : getAddress.city,
      customerPincode: index === "0" ? "" : getAddress.pincode,
      customerState: index === "0" ? "" : getAddress.state
    }));
  };

  // HSN search
  const searchHsn = useMemo(
    () =>
      debounce(async (index, code) => {
        if (!code || code.length < 2) {
          setHsnResults((prev) => ({ ...prev, [index]: [] }));
          setShowHsnDropdown((prev) => ({ ...prev, [index]: false }));
          return;
        }
        try {
          const res = await hsnApi.searchHsn(code, token);
          const list = res?.content || res?.data || (Array.isArray(res) ? res : []);
          setHsnResults((prev) => ({ ...prev, [index]: list }));
          setShowHsnDropdown((prev) => ({ ...prev, [index]: true }));
        } catch (error) {
          console.error("HSN search error:", error);
        }
      }, 300),
    [token]
  );

  const selectHsn = (index, hsn) => {
    const items = [...invoice.items];
    const taxRate = (hsn.gstRate || 0.18) * 100;
    items[index] = {
      ...items[index],
      hsnCode: hsn.hsnCode8 || "",
      description: hsn.productGroupName || items[index].description,
      taxRate: taxRate,
    };
    items[index].amount = items[index].quantity * items[index].rate;
    setInvoice((prev) => ({ ...prev, items }));
    setShowHsnDropdown((prev) => ({ ...prev, [index]: false }));
  };

  // Item management
  const updateItem = (index, field, value) => {
    const items = [...invoice.items];
    items[index][field] = value;

    if (field === "hsnCode") {
      searchHsn(index, value);
    }

    if (["quantity", "rate"].includes(field)) {
      items[index].amount = Number(items[index].quantity) * Number(items[index].rate);
    }

    setInvoice((prev) => ({ ...prev, items }));
  };

  const addItem = () => {
    setInvoice((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          id: Date.now(),
          description: "",
          hsnCode: "",
          quantity: 1,
          rate: 0,
          taxRate: 18,
          amount: 0,
        },
      ],
    }));
  };

  const removeItem = (index) => {
    if (invoice.items.length === 1) return;
    setInvoice((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  // Calculations
  const calculations = useMemo(() => {
    const subtotal = invoice.items.reduce((sum, item) => sum + (item.amount || 0), 0);
    const taxBreakdown = {};

    invoice.items.forEach((item) => {
      const taxRate = item.taxRate || 0;
      const taxAmount = (item.amount * taxRate) / 100;
      if (!taxBreakdown[taxRate]) {
        taxBreakdown[taxRate] = { rate: taxRate, amount: 0 };
      }
      taxBreakdown[taxRate].amount += taxAmount;
    });

    const totalTax = Object.values(taxBreakdown).reduce((sum, t) => sum + t.amount, 0);
    const total = subtotal + totalTax;

    return { subtotal, taxBreakdown, totalTax, total };
  }, [invoice.items]);

  // Logo handling
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setInvoice((prev) => ({ ...prev, logo: reader.result }));
    reader.readAsDataURL(file);
  };

  // Download PDF
  const downloadPdf = () => {
    const opt = {
      margin: 0.3,
      filename: `${invoice.invoiceNumber}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
    };
    html2pdf().from(invoiceRef.current).set(opt).save();
  };

  // Print
  // const handlePrint = () => {
  //   window.print();
  // };
  const handlePrint = async () => {
  const element = invoiceRef.current;

  const opt = {
    margin: 0.3,
    filename: `${invoice.invoiceNumber}.pdf`,
    image: { type: "jpeg", quality: 0.98 },
    html2canvas: {
      scale: 2,
      useCORS: true
    },
    jsPDF: {
      unit: "in",
      format: "a4",
      orientation: "portrait"
    }
  };

  const worker = html2pdf().set(opt).from(element).toPdf();
  const pdfBlob = await worker.output("blob");

  const blobUrl = URL.createObjectURL(pdfBlob);

  const printWindow = window.open(blobUrl);

  printWindow.onload = function () {
    printWindow.focus();
    printWindow.print();
  };
};

  // Save invoice state
  const [saving, setSaving] = useState(false);
  const [savedInvoiceId, setSavedInvoiceId] = useState(null);

  // Save Invoice to Backend
  const saveInvoice = async () => {
    if (!selectedAccount?.id) {
      toast.error("Please select a customer account");
      return;
    }

    setSaving(true);
    try {
      const invoiceData = {
        invoiceNumber: invoice.invoiceNumber,
        accountId: selectedAccount.id,
        organizationId: selectedOrg?.id || null,
        businessName: invoice.businessName,
        businessGstin: invoice.businessGstin,
        businessAddress: invoice.businessAddress,
        businessCity: invoice.businessCity,
        businessState: invoice.businessState,
        businessPincode: invoice.businessPincode,
        businessPhone: invoice.businessPhone,
        businessEmail: invoice.businessEmail,
        businessLogo: invoice.logo,
        customerName: invoice.customerName,
        customerGstin: invoice.customerGstin,
        customerAddress: invoice.customerAddress,
        customerCity: invoice.customerCity,
        customerState: invoice.customerState,
        customerPincode: invoice.customerPincode,
        customerPhone: invoice.customerPhone,
        customerEmail: invoice.customerEmail,
        invoiceDate: invoice.invoiceDate,
        dueDate: invoice.dueDate || null,
        items: invoice.items.map((item) => ({
          id: item.id,
          description: item.description,
          hsnCode: item.hsnCode,
          quantity: item.quantity,
          rate: item.rate,
          taxRate: item.taxRate,
          taxAmount: (item.amount * item.taxRate) / 100,
          amount: item.amount,
        })),
        subtotal: calculations.subtotal,
        totalTax: calculations.totalTax,
        totalAmount: calculations.total,
        taxBreakdown: Object.values(calculations.taxBreakdown).map((t) => ({
          rate: t.rate,
          amount: t.amount,
          cgst: t.amount / 2,
          sgst: t.amount / 2,
          igst: 0,
        })),
        notes: invoice.notes,
        terms: invoice.terms,
        status: "DRAFT",
        templateId: selectedTemplate,
      };

      let response;
      if (savedInvoiceId) {
        response = await invoiceApi.updateInvoice(savedInvoiceId, invoiceData, token);
      } else {
        response = await invoiceApi.createInvoice(invoiceData, token);
      }

      if (response?.success || response?.data) {
        const savedData = response.data || response;
        setSavedInvoiceId(savedData.id);
        toast.success(savedInvoiceId ? "Invoice updated successfully" : "Invoice saved successfully");
      } else {
        throw new Error(response?.message || "Failed to save invoice");
      }
    } catch (error) {
      console.error("Error saving invoice:", error);
      toast.error(error.message || "Failed to save invoice");
    } finally {
      setSaving(false);
    }
  };

  // Generate invoice number on mount
  useEffect(() => {
    const fetchInvoiceNumber = async () => {
      try {
        const response = await invoiceApi.generateInvoiceNumber("INV-", token);
        if (response?.data) {
          setInvoice((prev) => ({ ...prev, invoiceNumber: response.data }));
        }
      } catch (error) {
        console.error("Error generating invoice number:", error);
      }
    };
    if (token) {
      fetchInvoiceNumber();
    }
  }, [token]);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-30 px-6 py-3 flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-800">Invoice Generator</h1>

        {/* Template Selector */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <Palette size={16} className="text-gray-500" />
            <span className="text-sm text-gray-600">Template:</span>
            <div className="flex gap-1">
              {INVOICE_TEMPLATES.map((tmpl) => (
                <button
                  key={tmpl.id}
                  onClick={() => setSelectedTemplate(tmpl.id)}
                  className={`px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-all ${selectedTemplate === tmpl.id
                    ? "border-2 shadow-sm"
                    : "border-primary hover:border-primary"
                    }`}
                  style={{
                    borderColor: selectedTemplate === tmpl.id ? tmpl.color : undefined,
                    backgroundColor: selectedTemplate === tmpl.id ? `${tmpl.color}10` : undefined,
                    color: selectedTemplate === tmpl.id ? tmpl.color : "#6b7280",
                  }}
                >
                  {tmpl.name}
                </button>
              ))}
            </div>
          </div>

          <div className="h-6 w-px bg-gray-300"></div>

          <button
            onClick={saveInvoice}
            disabled={saving}
            className="flex items-center gap-2 px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-primary text-sm font-medium disabled:opacity-50"
          >
            {saving ? (
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
            ) : (
              <Save size={16} />
            )}
            {savedInvoiceId ? "Update" : "Save"}
          </button>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-primary text-sm font-medium"
          >
            <Printer size={16} />
            Print
          </button>
          <button
            onClick={downloadPdf}
            className="flex items-center gap-2 px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-primary text-sm font-medium"
          >
            <Download size={16} />
            Download PDF
          </button>
        </div>
      </div>

      <div className=" py-2 flex gap-2">
        {/* Left Panel - Form */}
        <div className="w-1/2 space-y-2">
          {/* Business Info (From) */}
          <div className="bg-white rounded-lg shadow-sm p-3">
            <h2 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
              From (Your Business)
            </h2>

            {/* Organization Selector */}
            {organizations.length > 0 && (
              <div className="mb-3">
                <select
                  className="w-full border border-gray-300 rounded-lg p-2 text-sm"
                  value={selectedOrg?.id || ""}
                  onChange={(e) => {
                    const org = organizations.find((o) => o.id === e.target.value);
                    if (org) selectOrganization(org);
                  }}
                >
                  <option value="">Select Organization</option>
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex gap-4">
              {/* Logo */}
              <div className="w-24">
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  className="hidden"
                />
                <div
                  onClick={() => logoInputRef.current.click()}
                  className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
                >
                  {invoice.logo ? (
                    <img src={invoice.logo} alt="Logo" className="max-w-full max-h-full object-contain" />
                  ) : (
                    <div className="text-center text-gray-400">
                      <Image size={24} className="mx-auto mb-1" />
                      <span className="text-xs">Add Logo</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Business Details */}
              <div className="flex-1 grid grid-cols-2 gap-2">
                <input
                  className="col-span-2 border border-gray-300 rounded p-2 text-sm"
                  placeholder="Business Name"
                  value={invoice.businessName}
                  onChange={(e) => setInvoice((prev) => ({ ...prev, businessName: e.target.value }))}
                />
                <input
                  className="border border-gray-300 rounded p-2 text-sm font-mono"
                  placeholder="GSTIN"
                  value={invoice.businessGstin}
                  onChange={(e) => setInvoice((prev) => ({ ...prev, businessGstin: e.target.value.toUpperCase() }))}
                />
                <input
                  className="border border-gray-300 rounded p-2 text-sm"
                  placeholder="Phone"
                  value={invoice.businessPhone}
                  onChange={(e) => setInvoice((prev) => ({ ...prev, businessPhone: e.target.value }))}
                />
                <input
                  className="col-span-2 border border-gray-300 rounded p-2 text-sm"
                  placeholder="Address"
                  value={invoice.businessAddress}
                  onChange={(e) => setInvoice((prev) => ({ ...prev, businessAddress: e.target.value }))}
                />
                <input
                  className="border border-gray-300 rounded p-2 text-sm"
                  placeholder="City"
                  value={invoice.businessCity}
                  onChange={(e) => setInvoice((prev) => ({ ...prev, businessCity: e.target.value }))}
                />
                <input
                  className="border border-gray-300 rounded p-2 text-sm"
                  placeholder="State"
                  value={invoice.businessState}
                  onChange={(e) => setInvoice((prev) => ({ ...prev, businessState: e.target.value }))}
                />
              </div>
            </div>
          </div>

          {/* Customer Info (To) */}
          <div className="bg-white rounded-lg shadow-sm p-3">
            <h2 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
              Bill To (Customer)
            </h2>

            {/* Customer Search */}
            <div className="relative mb-3">
              <div className="relative">
                {/* <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" /> */}
                <input
                  className="w-full border border-gray-300 rounded-lg p-2 pl-9 text-sm"
                  placeholder="Search existing customer or type new name..."
                  value={accountSearch}
                  onChange={(e) => handleAccountSearch(e.target.value)}
                  onBlur={() => setTimeout(() => setShowAccountDropdown(false), 200)}
                />
                {loadingAccounts && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                  </div>
                )}
              </div>

              {showAccountDropdown && accounts.length > 0 && (
                <div className="absolute left-0 top-full w-full bg-white border border-gray-200 shadow-lg rounded-lg mt-1 max-h-48 overflow-auto z-50">
                  {accounts.map((acc) => (
                    <div
                      key={acc.id}
                      onMouseDown={() => selectAccount(acc)}
                      className="p-3 hover:bg-blue-50 cursor-pointer border-b last:border-0"
                    >
                      <div className="font-medium text-sm text-gray-800">{acc.accountName || acc.name}</div>
                      <div className="text-xs text-gray-500">
                        {acc.email || acc.phoneNumber || acc.mobileNumber || acc.industry || ""}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2">
              {/* GST Selection - Show dropdown if multiple GST registrations */}
              {customerGstOptions.length > 1 ? (
                <select
                  className="border border-gray-300 rounded p-2 text-sm font-mono"
                  value={invoice.customerGstin}
                  onChange={(e) => handleGstSelection(e.target.value)}
                >
                  <option value="">Select GSTIN</option>
                  {customerGstOptions.map((gst, idx) => (
                    <option key={idx} value={gst.gstin}>
                      {gst.gstin} {gst.state ? `(${gst.state})` : ""}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  className="border border-gray-300 rounded p-2 text-sm font-mono"
                  placeholder="Customer GSTIN"
                  value={invoice.customerGstin}
                  onChange={(e) => setInvoice((prev) => ({ ...prev, customerGstin: e.target.value.toUpperCase() }))}
                />
              )}
              <input
                className="border border-gray-300 rounded p-2 text-sm"
                placeholder="Phone"
                value={invoice.customerPhone}
                onChange={(e) => setInvoice((prev) => ({ ...prev, customerPhone: e.target.value }))}
              />
              {customerAddressOptions.length > 1 ? (
                <select
                  className="col-span-2 border border-gray-300 rounded p-2 text-sm font-mono"
                  value={invoice.customerAddressId}
                  onChange={(e) => handleAddressSelection(e.target.value)}
                >
                  <option value="0">Select Address</option>
                  {customerAddressOptions.map((address, idx) => (
                    <option key={idx} value={address.id}>
                      {address.address} {address.city ? `(${address.city})` : ""}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  className="col-span-2 border border-gray-300 rounded p-2 text-sm"
                  placeholder="Address"
                  value={invoice.customerAddress}
                  onChange={(e) => setInvoice((prev) => ({ ...prev, customerAddress: e.target.value }))}
                />
              )}
              <input
                className="border border-gray-300 rounded p-2 text-sm"
                placeholder="City"
                value={invoice.customerCity}
                onChange={(e) => setInvoice((prev) => ({ ...prev, customerCity: e.target.value }))}
              />
              <input
                className="border border-gray-300 rounded p-2 text-sm"
                placeholder="State"
                value={invoice.customerState}
                onChange={(e) => setInvoice((prev) => ({ ...prev, customerState: e.target.value }))}
              />
            </div>
          </div>

          {/* Invoice Details */}
          <div className="bg-white rounded-lg shadow-sm p-3">
            <h2 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
              Invoice Details
            </h2>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Invoice Number</label>
                <input
                  className="w-full border border-gray-300 rounded p-2 text-sm font-mono"
                  value={invoice.invoiceNumber}
                  onChange={(e) => setInvoice((prev) => ({ ...prev, invoiceNumber: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Invoice Date</label>
                <input
                  type="date"
                  className="w-full border border-gray-300 rounded p-2 text-sm"
                  value={invoice.invoiceDate}
                  onChange={(e) => setInvoice((prev) => ({ ...prev, invoiceDate: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Due Date</label>
                <input
                  type="date"
                  className="w-full border border-gray-300 rounded p-2 text-sm"
                  value={invoice.dueDate}
                  onChange={(e) => setInvoice((prev) => ({ ...prev, dueDate: e.target.value }))}
                />
              </div>
            </div>
          </div>

          {/* Items */}
          <div className="bg-white rounded-lg shadow-sm p-3">
            <h2 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
              Items
            </h2>

            <div className="space-y-1">
              {invoice.items.map((item, index) => (
                <div key={item.id} className="bg-gray-50 rounded-lg p-2 relative">
                  {invoice.items.length > 1 && (
                    <button
                      onClick={() => removeItem(index)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <X size={14} />
                    </button>
                  )}

                  <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-4">
                      <label className="text-xs text-gray-500 block mb-1">Description</label>
                      <input
                        className="w-full border border-gray-300 rounded p-2 text-sm"
                        placeholder="Item description"
                        value={item.description}
                        onChange={(e) => updateItem(index, "description", e.target.value)}
                      />
                    </div>

                    <div className="col-span-2 relative">
                      <label className="text-xs text-gray-500 block mb-1">HSN/SAC Code</label>
                      <input
                        className="w-full border border-gray-300 rounded p-2 text-sm font-mono"
                        placeholder="HSN Code"
                        value={item.hsnCode}
                        onChange={(e) => updateItem(index, "hsnCode", e.target.value)}
                      />
                      {showHsnDropdown[index] && hsnResults[index]?.length > 0 && (
                        <div className="absolute left-0 top-full w-full bg-white border shadow-lg rounded mt-1 max-h-32 overflow-auto z-50">
                          {hsnResults[index].map((hsn, i) => (
                            <div
                              key={i}
                              onClick={() => selectHsn(index, hsn)}
                              className="p-2 hover:bg-blue-50 cursor-pointer text-xs border-b"
                            >
                              <div className="font-mono font-bold text-blue-600">{hsn.hsnCode8}</div>
                              <div className="text-gray-500 truncate">{hsn.productGroupName}</div>
                              <div className="text-green-600">GST: {(hsn.gstRate * 100).toFixed(0)}%</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="col-span-2">
                      <label className="text-xs text-gray-500 block mb-1">Qty</label>
                      <input
                        type="number"
                        min="1"
                        className="w-full border border-gray-300 rounded p-2 text-sm text-center"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, "quantity", Number(e.target.value))}
                      />
                    </div>

                    <div className="col-span-2">
                      <label className="text-xs text-gray-500 block mb-1">Rate (₹)</label>
                      <input
                        type="number"
                        min="0"
                        className="w-full border border-gray-300 rounded p-2 text-sm text-right"
                        value={item.rate}
                        onChange={(e) => updateItem(index, "rate", Number(e.target.value))}
                      />
                    </div>

                    <div className="col-span-2">
                      <label className="text-xs text-gray-500 block mb-1">Amount</label>
                      <div className="p-2 text-xs font-semibold text-gray-700 text-right">
                        ₹{item.amount.toFixed(2)}
                      </div>
                    </div>
                  </div>

                  <div className="mt-2 flex items-center gap-2">
                    <label className="text-xs text-gray-500">Tax Rate:</label>
                    <select
                      className="border border-gray-300 rounded p-1 text-xs"
                      value={item.taxRate}
                      onChange={(e) => updateItem(index, "taxRate", Number(e.target.value))}
                    >
                      <option value={0}>0%</option>
                      <option value={5}>5%</option>
                      <option value={12}>12%</option>
                      <option value={18}>18%</option>
                      <option value={28}>28%</option>
                    </select>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={addItem}
              className="mt-3 flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              <Plus size={16} />
              Add Item
            </button>
          </div>

          {/* Notes & Terms */}
          <div className="bg-white rounded-lg shadow-sm p-3">
            <h2 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
              Notes & Terms
            </h2>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Notes</label>
                <textarea
                  className="w-full border border-gray-300 rounded p-2 text-sm"
                  rows={2}
                  placeholder="Additional notes..."
                  value={invoice.notes}
                  onChange={(e) => setInvoice((prev) => ({ ...prev, notes: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Terms & Conditions</label>
                <textarea
                  className="w-full border border-gray-300 rounded p-2 text-sm"
                  rows={2}
                  value={invoice.terms}
                  onChange={(e) => setInvoice((prev) => ({ ...prev, terms: e.target.value }))}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Preview */}
        <div className="w-1/2">
          <div className="bg-white rounded-lg shadow-sm sticky top-18">
            <div ref={invoiceRef} className="print-area">
              {/* Standard Template - Clean Professional Look */}
              {selectedTemplate === "standard" && (
                <div className="p-2 bg-white">
                  {/* Header with accent bar */}
                  <div className="border-t-4 border-blue-500 pt-1 mb-2">
                    <div className="flex justify-between items-start">
                      <div className="flex items-start gap-2">
                        {invoice.logo ? <img src={invoice.logo} alt="Logo" className="w-16 h-16 object-contain" /> : <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center text-blue-500 text-2xl font-bold">{(invoice.businessName || "B")[0]}</div>}
                        <div>
                          <div className="text-xl font-bold text-gray-900">{invoice.businessName || "Your Business Name"}</div>
                          <div className="text-xs text-gray-600 mt-0">{[invoice.businessAddress, invoice.businessCity, invoice.businessState].filter(Boolean).join(", ")}</div>
                          {invoice.businessGstin && <div className="text-xs text-gray-500 font-mono mt-0">GSTIN: {invoice.businessGstin}</div>}
                          {invoice.businessPhone && <div className="text-xs text-gray-500">Tel: {invoice.businessPhone}</div>}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xl font-bold text-blue-600">INVOICE</div>
                        <div className="text-base font-mono text-gray-700 mt-0">{invoice.invoiceNumber}</div>
                        <div className="mt-0 text-xs text-gray-600">
                          <div>Date: <span className="font-medium">{invoice.invoiceDate ? new Date(invoice.invoiceDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-"}</span></div>
                          {invoice.dueDate && <div>Due: <span className="font-medium text-red-600">{new Date(invoice.dueDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</span></div>}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bill To Section */}
                  <div className="bg-gray-50 rounded-lg p-2 mb-2">
                    <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Bill To</div>
                    <div className="text-lg font-semibold text-gray-900">{invoice.customerName || "Customer Name"}</div>
                    {invoice.customerGstin && <div className="text-sm text-gray-600 font-mono">GSTIN: {invoice.customerGstin}</div>}
                    <div className="text-sm text-gray-600">{[invoice.customerAddress, invoice.customerCity, invoice.customerState].filter(Boolean).join(", ")}</div>
                    {invoice.customerPhone && <div className="text-sm text-gray-500">Tel: {invoice.customerPhone}</div>}
                  </div>

                  {/* Items Table */}
                  <table className="w-full mb-0 border-b">
                    <thead>
                      <tr className="bg-blue-600 text-white">
                        <th className="text-left px-3 py-2 text-xs font-semibold rounded-tl-lg">#</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold">Item & Description</th>
                        <th className="text-center px-3 py-2 text-xs font-semibold">HSN/SAC</th>
                        <th className="text-center px-3 py-2 text-xs font-semibold">Qty</th>
                        <th className="text-right px-3 py-2 text-xs font-semibold">Rate</th>
                        <th className="text-right px-3 py-2 text-xs font-semibold">Tax</th>
                        <th className="text-right px-3 py-2 text-xs font-semibold rounded-tr-lg">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoice.items.map((item, index) => (
                        <tr key={item.id} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                          <td className="px-3 py-1 text-xs text-gray-600">{index + 1}</td>
                          <td className="px-3 py-1 text-xs font-medium text-gray-900">{item.description || "-"}</td>
                          <td className="px-3 py-1 text-xs text-center font-mono text-gray-600">{item.hsnCode || "-"}</td>
                          <td className="px-3 py-1 text-xs text-center">{item.quantity}</td>
                          <td className="px-3 py-1 text-xs text-right">₹{item.rate.toFixed(2)}</td>
                          <td className="px-3 py-1 text-xs text-right text-gray-600">{item.taxRate}%</td>
                          <td className="px-3 py-1 text-xs text-right font-semibold">₹{item.amount.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Summary */}
                  <div className="flex justify-end mb-2">
                    <div className="w-72 text-xs">
                      <div className="flex justify-between py-1 px-3 border-b border-gray-200"><span className="text-gray-600">Subtotal</span><span className="font-medium">₹{calculations.subtotal.toFixed(2)}</span></div>
                      {Object.values(calculations.taxBreakdown).map((tax, i) => (<div key={i} className="flex justify-between pt-1 pb-2 px-3 border-b border-gray-200 text-gray-600"><span>CGST ({tax.rate / 2}%)</span><span>₹{(tax.amount / 2).toFixed(2)}</span></div>))}
                      {Object.values(calculations.taxBreakdown).map((tax, i) => (<div key={`sgst-${i}`} className="flex justify-between py-1 px-3 border-b border-gray-200 text-gray-600"><span>SGST ({tax.rate / 2}%)</span><span>₹{(tax.amount / 2).toFixed(2)}</span></div>))}
                      <div className="flex items-center justify-between py-1 px-3 bg-blue-500 text-white rounded-lg mt-2"><span className="font-bold">Total Amount</span><span className="font-bold text-lg">₹{calculations.total.toFixed(2)}</span></div>
                    </div>
                  </div>

                  {/* Notes & Terms */}
                  {(invoice.notes || invoice.terms) && (
                    <div className="border-t border-gray-200 pt-2 mt-2 space-y-1">
                      {invoice.notes && <div><h4 className="text-xs font-bold text-gray-500 uppercase mb-0">Notes</h4><p className="text-sm text-gray-600">{invoice.notes}</p></div>}
                      {invoice.terms && <div><h4 className="text-xs font-bold text-gray-500 uppercase mb-0">Terms & Conditions</h4><p className="text-sm text-gray-500">{invoice.terms}</p></div>}
                    </div>
                  )}

                  {/* Footer */}
                  <div className="mt-8 pt-2 border-t border-gray-200 flex justify-between items-end">
                    <div className="text-xs text-gray-400">Thank you for your business!</div>
                    <div className="text-right">
                      <div className="text-xs font-semibold text-gray-700">For {invoice.businessName || "Your Business"}</div>
                      <div className="mt-8 border-t border-gray-300 pt-1 px-6"><div className="text-xs text-gray-500">Authorized Signatory</div></div>
                    </div>
                  </div>
                </div>
              )}

              {/* Elegant Template - Sophisticated Purple Theme */}
              {selectedTemplate === "elegant" && (
                <div className="p-2 bg-white">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      {invoice.logo ? <img src={invoice.logo} alt="Logo" className="w-16 h-16 object-contain mb-0" /> : <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center text-violet-600 text-3xl font-serif font-bold mb-0">{(invoice.businessName || "B")[0]}</div>}
                      <div className="text-xl font-serif font-bold text-violet-900">{invoice.businessName || "Your Business Name"}</div>
                      <div className="text-xs text-violet-700 mt-0">{[invoice.businessAddress, invoice.businessCity, invoice.businessState].filter(Boolean).join(", ")}</div>
                      {invoice.businessGstin && <div className="text-xs text-violet-600 font-mono mt-0">GSTIN: {invoice.businessGstin}</div>}
                    </div>
                    <div className="text-right">
                      <div className="inline-block border-2 border-violet-300 rounded-lg p-2">
                        <div className="text-lg font-serif font-bold text-violet-700">TAX INVOICE</div>
                        <div className="text-violet-600 font-mono text-lg">{invoice.invoiceNumber}</div>
                      </div>
                      <div className="mt-2 text-sm">
                        <div className="text-violet-700">Dated: <span className="font-semibold">{invoice.invoiceDate ? new Date(invoice.invoiceDate).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" }) : "-"}</span></div>
                        {invoice.dueDate && <div className="text-violet-600">Payment Due: <span className="font-semibold">{new Date(invoice.dueDate).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })}</span></div>}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <div className="bg-white rounded-xl p-3 shadow-sm border border-violet-100">
                      <div className="text-xs font-bold text-violet-500 uppercase tracking-wider mb-3 flex items-center gap-2"><span className="w-8 h-0.5 bg-violet-300"></span>Bill To</div>
                      <div className="text-lg font-serif font-semibold text-gray-900">{invoice.customerName || "Customer Name"}</div>
                      {invoice.customerGstin && <div className="text-sm text-violet-600 font-mono">GSTIN: {invoice.customerGstin}</div>}
                      <div className="text-sm text-gray-600 mt-1">{[invoice.customerAddress, invoice.customerCity, invoice.customerState].filter(Boolean).join(", ")}</div>
                      {invoice.customerPhone && <div className="text-sm text-gray-500 mt-1">📞 {invoice.customerPhone}</div>}
                    </div>
                    <div className="bg-violet-600 rounded-xl p-3 text-white">
                      <div className="text-xs font-bold text-violet-200 uppercase tracking-wider mb-2">Amount Due</div>
                      <div className="text-3xl font-bold">₹{calculations.total.toFixed(2)}</div>
                      <div className="text-violet-200 text-sm mt-2">Including all taxes</div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-violet-100 mb-2">
                    <table className="w-full">
                      <thead><tr className="bg-violet-600 text-white"><th className="text-left px-2 py-1 text-xs font-semibold">Description</th><th className="text-center px-2 py-1 text-xs font-semibold">HSN</th><th className="text-center px-2 py-1 text-xs font-semibold">Qty</th><th className="text-right px-2 py-1 text-xs font-semibold">Rate</th><th className="text-right px-2 py-1 text-xs font-semibold">Tax</th><th className="text-right px-2 py-1 text-xs font-semibold">Amount</th></tr></thead>
                      <tbody>{invoice.items.map((item, index) => (<tr key={item.id} className={`border-b border-violet-50 ${index % 2 === 0 ? "bg-violet-50/30" : "bg-white"}`}><td className="px-2 py-1 text-xs font-medium">{item.description || "-"}</td><td className="px-2 py-1 text-xs text-center font-mono text-violet-600">{item.hsnCode || "-"}</td><td className="px-2 py-1 text-xs text-center">{item.quantity}</td><td className="px-2 py-1 text-xs text-right">₹{item.rate.toFixed(2)}</td><td className="px-2 py-1 text-xs text-right text-violet-600">{item.taxRate}%</td><td className="px-2 py-1 text-xs text-right font-semibold">₹{item.amount.toFixed(2)}</td></tr>))}</tbody>
                    </table>
                  </div>

                  <div className="flex justify-end mb-2">
                    <div className="w-80 bg-white rounded-xl p-2 shadow-sm border border-violet-100">
                      <div className="flex justify-between py-1 text-xs border-b border-violet-100"><span className="text-gray-600">Subtotal</span><span className="font-medium">₹{calculations.subtotal.toFixed(2)}</span></div>
                      {Object.values(calculations.taxBreakdown).map((tax, i) => (<div key={i} className="flex justify-between py-1 text-xs border-b border-violet-100 text-violet-600"><span>GST ({tax.rate}%)</span><span>₹{tax.amount.toFixed(2)}</span></div>))}
                      <div className="flex items-center justify-between py-1 text-xs mt-2 bg-violet-600 text-white rounded-lg px-3 -mx-1"><span className="font-bold">Grand Total</span><span className="font-semibold text-lg">₹{calculations.total.toFixed(2)}</span></div>
                    </div>
                  </div>

                  {(invoice.notes || invoice.terms) && (<div className="bg-white rounded-xl p-3 shadow-sm border border-violet-100 space-y-1">{invoice.notes && <div className="text-sm text-gray-600"><span className="font-semibold text-violet-700">Notes:</span> {invoice.notes}</div>}{invoice.terms && <div className="text-xs text-gray-500 border-t border-violet-100 pt-0">{invoice.terms}</div>}</div>)}

                  <div className="mt-10 flex justify-between items-end">
                    <div className="text-sm text-violet-400 font-serif italic">Thank you for your business</div>
                    <div className="text-center"><div className="text-sm font-serif font-semibold text-violet-800">For {invoice.businessName || "Your Business"}</div><div className="mt-10 border-t-2 border-violet-300 pt-2 px-8"><div className="text-xs text-gray-500">Authorized Signatory</div></div></div>
                  </div>
                </div>
              )}

              {/* Bold Template - Eye-catching Red Theme */}
              {selectedTemplate === "bold" && (
                <div className="p-2 bg-white">
                  <div className="bg-red-600 text-white p-2 mb-2">
                    <div className="flex justify-between items-center px-2">
                      <div className="flex items-start gap-4">
                        {invoice.logo ? <img src={invoice.logo} alt="Logo" className="w-16 h-16 object-contain bg-white rounded-lg p-1" /> : <div className="w-16 h-16 bg-white rounded-lg flex items-center justify-center text-red-600 text-2xl font-black">{(invoice.businessName || "B")[0]}</div>}
                        <div>
                          <div className="text-xl font-black">{invoice.businessName || "Your Business Name"}</div>
                          <div className="text-red-100 text-xs">{[invoice.businessAddress, invoice.businessCity].filter(Boolean).join(", ")}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-black">INVOICE</div>
                        <div className="text-red-200 text-sm">{invoice.invoiceNumber}</div>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 mb-2">
                    <div className="bg-gray-100 rounded-xl py-2 px-3">
                      <div className="text-xs text-gray-500 uppercase font-bold mb-0">Invoice Date</div>
                      <div className="text-lg font-semibold text-gray-900">{invoice.invoiceDate ? new Date(invoice.invoiceDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-"}</div>
                    </div>
                    <div className="bg-gray-100 rounded-xl py-2 px-3">
                      <div className="text-xs text-gray-500 uppercase font-bold mb-0">Due Date</div>
                      <div className="text-lg font-semibold text-red-600">{invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-"}</div>
                    </div>
                    <div className="bg-red-600 rounded-xl py-2 px-3 text-white">
                      <div className="text-xs text-red-200 uppercase font-bold mb-0">Amount Due</div>
                      <div className="text-lg font-semibold">₹{calculations.total.toFixed(2)}</div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-2 mb-2">
                    <div className="text-xs text-gray-500 uppercase font-bold mb-2">Bill To</div>
                    <div className="text-xl font-bold text-gray-900">{invoice.customerName || "Customer Name"}</div>
                    {invoice.customerGstin && <div className="text-sm font-mono text-red-600">GSTIN: {invoice.customerGstin}</div>}
                    <div className="text-sm text-gray-600">{[invoice.customerAddress, invoice.customerCity, invoice.customerState].filter(Boolean).join(", ")}</div>
                  </div>

                  <div className="rounded-xl overflow-hidden border-2 border-gray-200 mb-2">
                    <table className="w-full">
                      <thead><tr className="bg-gray-900 text-white"><th className="text-left px-2 py-1 text-xs font-bold uppercase">Item</th><th className="text-center px-2 py-1 text-xs font-bold uppercase">HSN</th><th className="text-center px-2 py-1 text-xs font-bold uppercase">Qty</th><th className="text-right px-2 py-1 text-xs font-bold uppercase">Rate</th><th className="text-right px-2 py-1 text-xs font-bold uppercase">Tax</th><th className="text-right px-2 py-1 text-xs font-bold uppercase">Amount</th></tr></thead>
                      <tbody>{invoice.items.map((item, index) => (<tr key={item.id} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}><td className="px-2 py-1 text-xs font-semibold">{item.description || "-"}</td><td className="px-2 py-1 text-xs text-center font-mono">{item.hsnCode || "-"}</td><td className="px-2 py-1 text-xs text-center font-semibold">{item.quantity}</td><td className="px-2 py-1 text-xs text-right">₹{item.rate.toFixed(2)}</td><td className="px-2 py-1 text-xs text-right text-red-600 font-semibold">{item.taxRate}%</td><td className="px-2 py-1 text-xs text-right font-semibold">₹{item.amount.toFixed(2)}</td></tr>))}</tbody>
                    </table>
                  </div>

                  <div className="flex justify-end mb-2">
                    <div className="w-72">
                      <div className="flex justify-between px-2 py-2 text-xs bg-gray-100 rounded-lg mb-0"><span className="font-semibold">Subtotal</span><span className="font-bold">₹{calculations.subtotal.toFixed(2)}</span></div>
                      {Object.values(calculations.taxBreakdown).map((tax, i) => (<div key={i} className="flex justify-between py-2 px-2 text-xs text-red-600"><span className="font-semibold">GST ({tax.rate}%)</span><span className="font-bold">₹{tax.amount.toFixed(2)}</span></div>))}
                      <div className="flex items-center justify-between py-2 px-2 bg-red-600 text-white rounded-xl mt-0"><span className="font-black text-sm">TOTAL</span><span className="font-black text-sm">₹{calculations.total.toFixed(2)}</span></div>
                    </div>
                  </div>

                  {(invoice.notes || invoice.terms) && (<div className="bg-gray-100 rounded-xl p-2 space-y-1">{invoice.notes && <div className="text-xs"><span className="font-bold text-red-600">Note:</span> {invoice.notes}</div>}{invoice.terms && <div className="text-xs text-gray-500">{invoice.terms}</div>}</div>)}

                  <div className="mt-1 text-right"><div className="font-bold text-gray-900">For {invoice.businessName || "Your Business"}</div><div className="mt-8 inline-block border-t-2 border-red-600 pt-1 px-8"><div className="text-xs text-gray-500 font-semibold">Authorized Signatory</div></div></div>
                </div>
              )}

              {/* Simple Template - Minimalist Black & White */}
              {selectedTemplate === "simple" && (
                <div className="p-2 bg-white font-sans">
                  <div className="border-b border-gray-900 pb-2 mb-2">
                    <div className="flex justify-between items-start">
                      <div>
                        {invoice.logo && <img src={invoice.logo} alt="Logo" className="w-16 h-16 object-contain mb-0 grayscale" />}
                        <div className="text-base font-bold text-gray-900 uppercase tracking-wider">{invoice.businessName || "Your Business"}</div>
                        <div className="text-xs text-gray-600">{[invoice.businessAddress, invoice.businessCity, invoice.businessState].filter(Boolean).join(" | ")}</div>
                        {invoice.businessGstin && <div className="text-xs text-gray-500">GSTIN: {invoice.businessGstin}</div>}
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-light text-gray-900 uppercase tracking-widest">Invoice</div>
                        <div className="text-gray-600 font-mono mt-0">{invoice.invoiceNumber}</div>
                        <div className="text-sm text-gray-500 mt-0">{invoice.invoiceDate ? new Date(invoice.invoiceDate).toLocaleDateString("en-IN") : "-"}</div>
                      </div>
                    </div>
                  </div>

                  <div className="mb-2">
                    <div className="text-xs text-gray-400 uppercase tracking-widest mb-2">Billed To</div>
                    <div className="text-base font-semibold text-gray-900">{invoice.customerName || "Customer Name"}</div>
                    {invoice.customerGstin && <div className="text-xs text-gray-600">GSTIN: {invoice.customerGstin}</div>}
                    <div className="text-xs text-gray-500">{[invoice.customerAddress, invoice.customerCity].filter(Boolean).join(", ")}</div>
                  </div>

                  <table className="w-full mb-2">
                    <thead><tr className="border-y border-gray-900"><th className="text-left py-1 text-xs uppercase tracking-wider">Description</th><th className="text-center py-1 text-xs uppercase tracking-wider">Qty</th><th className="text-right py-1 text-xs uppercase tracking-wider">Rate</th><th className="text-right py-1 text-xs uppercase tracking-wider">Amount</th></tr></thead>
                    <tbody>{invoice.items.map((item) => (<tr key={item.id} className="border-b border-gray-200"><td className="py-1"><div className="font-medium text-xs">{item.description || "-"}</div><div className="text-xs text-gray-400">HSN: {item.hsnCode || "-"} • Tax: {item.taxRate}%</div></td><td className="py-1 text-xs text-center">{item.quantity}</td><td className="py-1 text-xs text-right">₹{item.rate.toFixed(2)}</td><td className="py-1 text-right text-xs font-semibold">₹{item.amount.toFixed(2)}</td></tr>))}</tbody>
                  </table>

                  <div className="flex justify-end mb-2">
                    <div className="w-64">
                      <div className="flex justify-between py-1 text-xs"><span className="text-gray-500">Subtotal</span><span>₹{calculations.subtotal.toFixed(2)}</span></div>
                      {Object.values(calculations.taxBreakdown).map((tax, i) => (<div key={i} className="flex justify-between py-1 text-xs text-gray-500"><span>GST ({tax.rate}%)</span><span>₹{tax.amount.toFixed(2)}</span></div>))}
                      <div className="flex justify-between py-1 border-t border-gray-900 font-bold text-sm"><span>Total</span><span>₹{calculations.total.toFixed(2)}</span></div>
                    </div>
                  </div>

                  {invoice.notes && <div className="text-sm text-gray-500 mb-1">{invoice.notes}</div>}
                  {invoice.terms && <div className="text-xs text-gray-400 border-t border-gray-200 pt-1">{invoice.terms}</div>}

                  <div className="mt-8 text-right"><div className="inline-block text-center"><div className="w-40 border-t border-gray-400 pt-0"><div className="text-xs text-gray-500">Authorized Signature</div></div></div></div>
                </div>
              )}

              {/* Corporate Template - Business Formal Teal Theme */}
              {selectedTemplate === "corporate" && (
                <div className="p-2 bg-white">
                  <div className="flex justify-between items-start mb-2 pb-1 border-b-4 border-cyan-600">
                    <div className="flex items-center gap-2">
                      {invoice.logo ? <img src={invoice.logo} alt="Logo" className="w-20 h-20 object-contain" /> : <div className="w-20 h-20 bg-cyan-600 rounded flex items-center justify-center text-white text-3xl font-bold">{(invoice.businessName || "B")[0]}</div>}
                      <div>
                        <div className="text-xl font-bold text-gray-900">{invoice.businessName || "Your Business Name"}</div>
                        <div className="text-xs text-gray-600">{[invoice.businessAddress, invoice.businessCity, invoice.businessState].filter(Boolean).join(", ")}</div>
                        {invoice.businessGstin && <div className="text-xs text-cyan-600 font-mono">GSTIN: {invoice.businessGstin}</div>}
                        {invoice.businessPhone && <div className="text-xs text-gray-500">Tel: {invoice.businessPhone} | Email: {invoice.businessEmail || "-"}</div>}
                      </div>
                    </div>
                    <div className="bg-cyan-600 text-white px-3 py-2 rounded-lg text-right">
                      <div className="text-xl font-bold">TAX INVOICE</div>
                      <div className="text-cyan-100 font-mono text-lg">{invoice.invoiceNumber}</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <div>
                      <div className="text-xs font-bold text-cyan-600 uppercase tracking-wider mb-2 border-l-4 border-cyan-600 pl-3">Bill To</div>
                      <div className="text-base font-bold text-gray-900">{invoice.customerName || "Customer Name"}</div>
                      {invoice.customerGstin && <div className="text-xs text-gray-600 font-mono">GSTIN: {invoice.customerGstin}</div>}
                      <div className="text-xs text-gray-600">{[invoice.customerAddress, invoice.customerCity, invoice.customerState].filter(Boolean).join(", ")}</div>
                      {invoice.customerPhone && <div className="text-xs text-gray-500">Tel: {invoice.customerPhone}</div>}
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div><div className="text-gray-500 text-xs">Invoice Date</div><div className="font-semibold text-xs">{invoice.invoiceDate ? new Date(invoice.invoiceDate).toLocaleDateString("en-IN") : "-"}</div></div>
                        <div><div className="text-gray-500 text-xs">Due Date</div><div className="font-semibold text-xs text-red-600">{invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString("en-IN") : "-"}</div></div>
                        <div className="col-span-2 text-xs"><div className="text-gray-500">Place of Supply</div><div className="font-semibold text-xs">{invoice.customerState || "-"}</div></div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg overflow-hidden border border-gray-200 mb-2">
                    <table className="w-full">
                      <thead><tr className="bg-cyan-600 text-white"><th className="text-left px-2 py-1 text-xs font-semibold">S.No</th><th className="text-left px-2 py-1 text-xs font-semibold">Description of Goods/Services</th><th className="text-center px-2 py-1 text-xs font-semibold">HSN/SAC</th><th className="text-center px-2 py-1 text-xs font-semibold">Qty</th><th className="text-right px-2 py-1 text-xs font-semibold">Unit Price</th><th className="text-right px-2 py-1 text-xs font-semibold">Tax Rate</th><th className="text-right px-2 py-1 text-xs font-semibold">Amount</th></tr></thead>
                      <tbody>{invoice.items.map((item, index) => (<tr key={item.id} className="border-b border-gray-100 hover:bg-cyan-50"><td className="px-2 py-1 text-xs text-center">{index + 1}</td><td className="px-2 py-1 text-xs font-medium">{item.description || "-"}</td><td className="px-2 py-1 text-xs text-center font-mono text-cyan-700">{item.hsnCode || "-"}</td><td className="px-2 py-1 text-xs text-center">{item.quantity}</td><td className="px-2 py-1 text-xs text-right">₹{item.rate.toFixed(2)}</td><td className="px-2 py-1 text-xs text-right">{item.taxRate}%</td><td className="px-2 py-1 text-xs text-right font-semibold">₹{item.amount.toFixed(2)}</td></tr>))}</tbody>
                    </table>
                  </div>

                  <div className="flex justify-end mb-2">
                    <div className="w-80 border border-gray-200 rounded-lg overflow-hidden">
                      <div className="flex justify-between px-2 py-1 text-xs border-b border-gray-200 bg-gray-50"><span className="text-gray-600">Taxable Amount</span><span className="font-medium">₹{calculations.subtotal.toFixed(2)}</span></div>
                      {Object.values(calculations.taxBreakdown).map((tax, i) => (<><div key={`cgst-${i}`} className="flex px-2 py-1 text-xs justify-between border-b border-gray-200"><span className="text-gray-600">CGST @ {tax.rate / 2}%</span><span>₹{(tax.amount / 2).toFixed(2)}</span></div><div key={`sgst-${i}`} className="flex justify-between px-2 py-1 text-xs border-b border-gray-200"><span className="text-gray-600">SGST @ {tax.rate / 2}%</span><span>₹{(tax.amount / 2).toFixed(2)}</span></div></>))}
                      <div className="flex justify-between items-center px-2 py-1 text-xs bg-cyan-600 text-white"><span className="font-bold">Total Amount</span><span className="font-bold text-base">₹{calculations.total.toFixed(2)}</span></div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 mt-2 pt-2 border-t border-gray-200">
                    <div>
                      {invoice.notes && <div className="mb-2"><div className="text-xs font-semibold text-cyan-600 uppercase mb-0">Notes</div><div className="text-xs text-gray-600">{invoice.notes}</div></div>}
                      {invoice.terms && <div><div className="text-xs font-bold text-cyan-600 uppercase mb-0">Terms & Conditions</div><div className="text-xs text-gray-500">{invoice.terms}</div></div>}
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-sm text-gray-900">For {invoice.businessName || "Your Business"}</div>
                      <div className="mt-12 inline-block border-t-2 border-cyan-600 pt-0 px-6"><div className="text-xs text-gray-500">Authorized Signatory</div></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .print-area, .print-area * { visibility: visible; }
          .print-area { position: absolute; left: 0; top: 0; width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default InvoiceGenerator;
